import { createIdempotencyKey } from '../api';
import { createSignaturePad } from '../components/signature';
import { mountTurnstile, type TurnstileControl } from '../turnstile';
import { renderApplyPage as renderLegacyApplyPage } from './apply';

type FormField = {
  fieldname: string;
  label: string;
  fieldtype: string;
  required?: number;
  options?: string[];
  placeholder?: string;
  description?: string;
};

type FormLayout = {
  preset?: string;
  source?: string;
  preserve_structure?: boolean;
};

type FormSnapshot = {
  scopeType: string;
  company: string;
  outlet: string;
  version: number;
  form: {
    template_name: string;
    route: string;
    introduction: string;
    schema: { version: number; fields: FormField[]; layout?: FormLayout };
    submit_label: string;
    success_title: string;
    success_message: string;
    allow_multiple: number;
  };
};

type ApiEnvelope<T> = { ok: boolean; data?: T; error?: { message?: string } };

const OUTLET_NAMES: Record<string, string> = {
  KCH: 'Royal Richmond Square',
  JDBM: 'Jalan Datuk Bandar Mustapha',
  BTU: 'SKONE-BTU',
};

const API_ALIASES: Record<string, string> = {
  full_name: 'name',
  ic_number: 'icNumber',
  date_of_birth: 'dateOfBirth',
  street_address: 'streetAddress',
  postcode: 'zip',
  emergency_contact: 'emergencyContact',
  employment_type: 'employmentType',
  available_time: 'preferredWorkTime',
  date_available: 'dateAvailable',
  expected_hourly_rate: 'expectedHourlyRate',
  expected_salary: 'expectedSalary',
  resume: 'cv',
  declaration: 'declarationAccepted',
  signature_date: 'declarationDate',
};

const WORK_FIELD_MAP: Record<string, string> = {
  employer: 'employer',
  telephone: 'telephone',
  from: 'from',
  to: 'to',
  position: 'position',
  pay: 'pay',
  reason: 'reason',
  may_contact: 'mayContact',
};

const WORK_SUFFIX_LABELS: Record<string, string> = {
  employer: 'Employer',
  telephone: 'Telephone',
  from: 'From',
  to: 'To',
  position: 'Position / Job Title',
  pay: 'Pay',
  reason: 'Reason for Leaving',
  may_contact: 'May We Contact',
};

const EDUCATION_PREFIXES: Array<[string, string]> = [
  ['high_school', 'High School'],
  ['college', 'College / University'],
  ['trade_school', 'Trade School'],
  ['other_education', 'Other'],
];

export async function renderDynamicApplyPage(root: HTMLElement): Promise<void> {
  const params = new URLSearchParams(location.search);
  const outletCode = (params.get('outlet') || 'KCH').toUpperCase();
  root.innerHTML = '<main class="page application-page"><div class="paper application-paper"><div class="form-loading">Loading application form…</div></div></main>';

  try {
    const response = await fetch(`/api/form-config?outlet=${encodeURIComponent(outletCode)}`, { headers: { Accept: 'application/json' } });
    const body = await response.json() as ApiEnvelope<FormSnapshot>;
    if (!response.ok || !body.ok || !body.data?.form?.schema?.fields?.length) {
      renderLegacyApplyPage(root);
      return;
    }
    renderPublishedForm(root, body.data, outletCode);
  } catch {
    renderLegacyApplyPage(root);
  }
}

function renderPublishedForm(root: HTMLElement, snapshot: FormSnapshot, outletCode: string): void {
  const fields = snapshot.form.schema.fields;
  const outletName = snapshot.outlet || OUTLET_NAMES[outletCode] || outletCode;
  const isPdfLayout = snapshot.form.schema.layout?.preset === 'stupiak_job_application_v1';
  const body = isPdfLayout ? renderStupiakPdfLayout(fields) : fields.map(renderField).join('');

  root.innerHTML = `
    <main class="page application-page">
      <form class="paper application-paper dynamic-application-paper${isPdfLayout ? ' dynamic-application-paper-pdf' : ''}" id="applyForm">
        <div class="masthead${isPdfLayout ? ' dynamic-pdf-masthead' : ''}">
          <div><div class="brand-cn">阿笨的猪堡店</div><div class="brand-en">STUPIAK'S PORK BURGER</div></div>
          <div class="title-lockup">
            <div class="form-title">${safe(isPdfLayout ? 'Job Application' : snapshot.form.template_name || 'Job Application')}</div>
            <div class="form-subtitle">${safe(outletName)} · v${safe(snapshot.version)}</div>
          </div>
        </div>
        ${snapshot.form.introduction && !isPdfLayout ? `<div class="dynamic-form-intro">${safe(snapshot.form.introduction)}</div>` : ''}
        <input type="hidden" name="outletCode" value="${safeAttr(outletCode)}">
        <input type="hidden" name="outletName" value="${safeAttr(outletName)}">
        <input type="hidden" name="locationWorkTime" value="">
        ${body}
        <input class="honeypot" name="website" tabindex="-1" autocomplete="off">
        <div class="dynamic-public-footer">
          <div id="turnstileWidget" class="turnstile"></div>
          <div class="privacy">Shortlisted applicants will be contacted through WhatsApp.</div>
          <div id="result" class="result"></div>
          <div class="actions"><button class="submit" id="submitButton" type="submit">${safe(snapshot.form.submit_label || 'Submit application')}</button></div>
        </div>
      </form>
    </main>`;

  const form = root.querySelector<HTMLFormElement>('#applyForm')!;
  const signatureCanvas = root.querySelector<HTMLCanvasElement>('[data-signature-canvas]');
  const signaturePad = signatureCanvas ? createSignaturePad(signatureCanvas) : null;
  let turnstile: TurnstileControl = { getToken: () => '', reset: () => undefined };

  if (signaturePad) {
    root.querySelector('[data-clear-signature]')?.addEventListener('click', signaturePad.clear);
  }
  root.querySelectorAll<HTMLInputElement>('input[type="date"][name="declarationDate"]').forEach((input) => {
    if (!input.value) input.value = today();
  });

  mountTurnstile(root.querySelector<HTMLElement>('#turnstileWidget')!, 'application_submit')
    .then((control) => { turnstile = control; })
    .catch((error) => showResult(root, 'error', error instanceof Error ? error.message : String(error)));

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    clearResult(root);
    const validation = validateChoiceGroups(root, fields);
    if (validation) {
      showResult(root, 'error', validation);
      return;
    }
    if (signaturePad && !signaturePad.hasInk()) {
      showResult(root, 'error', 'Please sign inside the signature box.');
      return;
    }

    const button = root.querySelector<HTMLButtonElement>('#submitButton')!;
    const cv = root.querySelector<HTMLInputElement>('input[name="cv"]');
    if (cv?.files?.[0] && cv.files[0].size > 8 * 1024 * 1024) {
      showResult(root, 'error', 'CV must be 8 MB or smaller.');
      return;
    }

    const data = new FormData(form);
    normaliseChoiceValues(data, root, fields);
    if (signaturePad) data.set('signatureDataUrl', signaturePad.toDataUrl());
    data.set('turnstileToken', turnstile.getToken());
    data.set('idempotencyKey', createIdempotencyKey('application'));
    data.set('workExperiencesJson', JSON.stringify(collectWork(root, fields)));
    data.set('educationJson', JSON.stringify(collectEducation(root)));
    data.set('answersJson', JSON.stringify(collectAnswers(root, fields)));

    button.disabled = true;
    button.textContent = 'Submitting…';
    try {
      const response = await fetch('/api/application', { method: 'POST', body: data });
      const body = await response.json() as ApiEnvelope<{ applicationId: string }>;
      if (!response.ok || !body.ok) throw new Error(body.error?.message || 'Submission failed.');
      showResult(root, 'success', `<b>${safe(snapshot.form.success_title || 'Application received')}</b><br>${safe(snapshot.form.success_message || '')}<br>Application ID: ${safe(body.data?.applicationId || '-')}`);
      button.textContent = 'Submitted';
      Array.from(form.elements).forEach((element) => ((element as HTMLInputElement).disabled = true));
    } catch (error) {
      showResult(root, 'error', safe(error instanceof Error ? error.message : String(error)));
      turnstile.reset();
      button.disabled = false;
      button.textContent = snapshot.form.submit_label || 'Submit application';
    }
  });
}

function parseWorkField(fieldname: string): { index: number; suffix: string } | null {
  const match = /^work_(\d+)_(.+)$/.exec(fieldname);
  const indexText = match?.[1];
  const suffix = match?.[2];
  if (!indexText || !suffix) return null;
  return { index: Number(indexText), suffix };
}

function workGroupLabel(index: number): string {
  return index === 1 ? 'Current or Most Recent' : `Prior ${index - 1}`;
}

function humaniseKey(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function workItemLabel(field: FormField | undefined, suffix: string): string {
  const cleaned = String(field?.label || '')
    .replace(/^Work Experience\s+\d+\s*[-:]\s*/i, '')
    .replace(/\s*\*\s*$/, '')
    .trim();
  return cleaned || WORK_SUFFIX_LABELS[suffix] || humaniseKey(suffix);
}

function getWorkGroups(fields: FormField[]): Array<{ index: number; fields: Map<string, FormField> }> {
  const groups = new Map<number, Map<string, FormField>>();
  for (const field of fields) {
    const parsed = parseWorkField(field.fieldname);
    if (!parsed) continue;
    if (!groups.has(parsed.index)) groups.set(parsed.index, new Map());
    groups.get(parsed.index)!.set(parsed.suffix, field);
  }
  return Array.from(groups.entries())
    .sort(([left], [right]) => left - right)
    .map(([index, groupFields]) => ({ index, fields: groupFields }));
}

function getWorkSuffixes(fields: FormField[]): string[] {
  const suffixes: string[] = [];
  const seen = new Set<string>();
  for (const field of fields) {
    const parsed = parseWorkField(field.fieldname);
    if (!parsed || seen.has(parsed.suffix)) continue;
    seen.add(parsed.suffix);
    suffixes.push(parsed.suffix);
  }
  return suffixes;
}

function renderStupiakPdfLayout(fields: FormField[]): string {
  const fieldMap = new Map(fields.map((field) => [field.fieldname, field]));
  const get = (name: string) => fieldMap.get(name);
  const cell = (name: string, label = '', className = '') => renderPdfCell(get(name), label, className);
  const workGroups = getWorkGroups(fields);
  const workSuffixes = getWorkSuffixes(fields);
  const educationRows: Array<[string, string]> = [
    ['High School', 'high_school'],
    ['College / University', 'college'],
    ['Trade School', 'trade_school'],
    ['Other', 'other_education'],
  ];

  const workRows = workSuffixes.filter((suffix) => suffix !== 'from' && suffix !== 'to').map((suffix) => {
    const representative = workGroups.find((group) => group.fields.has(suffix))?.fields.get(suffix);
    return `<tr><th>${safe(workItemLabel(representative, suffix))}</th>${workGroups.map((group) => renderPdfCell(group.fields.get(suffix), '')).join('')}</tr>`;
  }).join('');
  const datesRow = workGroups.some((group) => group.fields.has('from') || group.fields.has('to')) ? `
    <tr><th>Dates of Employment</th>${workGroups.map((group) => `
      <td><div class="dynamic-pdf-split">
        ${group.fields.has('from') ? `<div><span>From</span>${renderControl(group.fields.get('from'), false)}</div>` : ''}
        ${group.fields.has('to') ? `<div><span>To</span>${renderControl(group.fields.get('to'), false)}</div>` : ''}
      </div></td>`).join('')}</tr>` : '';
  const education = educationRows.map(([label, prefix]) => `
    <tr><th>${safe(label)}</th>${cell(`${prefix}_name`)}${cell(`${prefix}_last_year`)}${cell(`${prefix}_degree`)}${cell(`${prefix}_major`)}</tr>`).join('');

  return `
    <div class="dynamic-pdf-layout">
      <div class="dynamic-pdf-section">Personal Information</div>
      <div class="dynamic-pdf-table-wrap">
        <table class="dynamic-pdf-table dynamic-pdf-personal"><tbody>
          <tr>${cell('full_name', 'Name', 'pdf-span-3')}${cell('email')}</tr>
          <tr>${cell('ic_number', 'IC Number', 'pdf-span-2')}${cell('date_of_birth')}${cell('gender')}</tr>
          <tr>${cell('street_address', '', 'pdf-span-2')}${cell('city')}${cell('postcode', 'Zip')}</tr>
          <tr>${cell('home_phone')}${cell('mobile')}${cell('emergency_contact', '', 'pdf-span-2')}</tr>
          <tr>${cell('position', 'What position are you applying for?', 'pdf-span-2')}${cell('employment_type', 'Applying for Full Time / Part Time', 'pdf-span-2')}</tr>
          <tr>${cell('expected_hourly_rate')}${cell('expected_salary')}${cell('date_available')}${cell('available_time')}</tr>
        </tbody></table>
      </div>

      <div class="dynamic-pdf-section">Prior Work Experience</div>
      ${workGroups.length ? `<div class="dynamic-pdf-table-wrap dynamic-work-desktop">
        <table class="dynamic-pdf-table dynamic-pdf-matrix">
          <thead><tr><th></th>${workGroups.map((group) => `<th>${safe(workGroupLabel(group.index))}</th>`).join('')}</tr></thead>
          <tbody>${workRows}${datesRow}</tbody>
        </table>
      </div>` : ''}

      <div class="dynamic-pdf-section">Education</div>
      <div class="dynamic-pdf-table-wrap">
        <table class="dynamic-pdf-table dynamic-pdf-education">
          <thead><tr><th></th><th>Name / Location</th><th>Last Year Complete</th><th>Degree</th><th>Major</th></tr></thead>
          <tbody>${education}</tbody>
        </table>
      </div>

      <div class="dynamic-pdf-table-wrap">
        <table class="dynamic-pdf-table dynamic-pdf-skills"><tbody>
          <tr><th>List any applicable special skills, training or proficiencies.</th>${cell('skills', '', 'pdf-span-3')}</tr>
          <tr><th>CV / Resume</th>${cell('resume', '', 'pdf-span-3')}</tr>
        </tbody></table>
      </div>

      <div class="dynamic-pdf-declaration">
        <div>${renderControl(get('declaration'), false)}</div>
        <div><div class="dynamic-pdf-cell-label">Signature</div>${renderControl(get('signature'), false)}</div>
        <div><div class="dynamic-pdf-cell-label">Date</div>${renderControl(get('signature_date'), false)}</div>
      </div>
    </div>`;
}

function renderPdfCell(field: FormField | undefined, label = '', className = ''): string {
  if (!field) return `<td class="${safeAttr(className)}"></td>`;
  const visibleLabel = label || '';
  return `<td class="${safeAttr(className)}"><div class="dynamic-pdf-cell-label">${safe(visibleLabel)}${visibleLabel && field.required ? ' *' : ''}</div>${renderControl(field, false)}</td>`;
}

function renderField(field: FormField): string {
  if (field.fieldtype === 'Section Break') {
    const description = field.description ? `<small class="dynamic-field-description">${safe(field.description)}</small>` : '';
    return `<div class="section-title dynamic-section">${safe(field.label || 'Section')}</div>${description}`;
  }
  return `<div class="dynamic-field${['Radio', 'Checkbox Group', 'Check', 'Signature', 'Attach', 'Text', 'Small Text'].includes(field.fieldtype) ? ' dynamic-field-full' : ''}">${renderControl(field, true)}</div>`;
}

function renderControl(field: FormField | undefined, showLabel: boolean): string {
  if (!field) return '';
  const fieldname = API_ALIASES[field.fieldname] || field.fieldname;
  const required = field.required ? ' required' : '';
  const requiredMark = field.required ? ' *' : '';
  const label = safe(field.label || 'Untitled field');
  const labelMarkup = showLabel ? `<label>${label}${requiredMark}</label>` : '';
  const description = field.description ? `<small class="dynamic-field-description">${safe(field.description)}</small>` : '';

  if (field.fieldtype === 'Radio' || field.fieldtype === 'Checkbox Group') {
    const inputType = field.fieldtype === 'Radio' ? 'radio' : 'checkbox';
    const options = (field.options || []).map((option) => `
      <label class="dynamic-choice"><input type="${inputType}" name="${safeAttr(fieldname)}" value="${safeAttr(option)}"${required && inputType === 'radio' ? ' required' : ''}><span>${safe(option)}</span></label>`).join('');
    return `<div class="dynamic-control-choice" data-choice-group="${safeAttr(fieldname)}" data-required="${field.required ? 'true' : 'false'}">${labelMarkup}${description}<div class="dynamic-choices">${options}</div></div>`;
  }
  if (field.fieldtype === 'Check') {
    return `<label class="dynamic-choice"><input type="checkbox" name="${safeAttr(fieldname)}" value="true"${required}><span>${label}${requiredMark}</span></label>${description}`;
  }
  if (field.fieldtype === 'Signature') {
    return `${labelMarkup}<div class="signature-box"><canvas data-signature-canvas></canvas><div class="sig-tools"><span>Sign with finger, mouse or trackpad.</span><button data-clear-signature type="button">Clear</button></div></div>`;
  }
  if (field.fieldtype === 'Select') {
    const options = (field.options || []).map((option) => `<option value="${safeAttr(option)}">${safe(option)}</option>`).join('');
    return `${labelMarkup}${description}<select name="${safeAttr(fieldname)}"${required}><option value="">Select</option>${options}</select>`;
  }
  if (field.fieldtype === 'Attach') {
    return `${labelMarkup}${description}<input type="file" name="${safeAttr(fieldname)}" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"${required}>`;
  }

  const inputType = field.fieldtype === 'Phone' ? 'tel' : field.fieldtype === 'Email' ? 'email' : field.fieldtype === 'Date' ? 'date' : 'text';
  const placeholder = field.placeholder ? ` placeholder="${safeAttr(field.placeholder)}"` : '';
  if (field.fieldtype === 'Text' || field.fieldtype === 'Small Text') {
    return `${labelMarkup}${description}<textarea name="${safeAttr(fieldname)}"${placeholder}${required}></textarea>`;
  }
  return `${labelMarkup}${description}<input type="${inputType}" name="${safeAttr(fieldname)}"${placeholder}${required}>`;
}

function validateChoiceGroups(root: HTMLElement, fields: FormField[]): string {
  for (const field of fields) {
    if (!field.required || field.fieldtype !== 'Checkbox Group') continue;
    const name = API_ALIASES[field.fieldname] || field.fieldname;
    if (!root.querySelector<HTMLInputElement>(`input[name="${cssEscape(name)}"]:checked`)) {
      return `Please choose at least one option for ${field.label}.`;
    }
  }
  return '';
}

function normaliseChoiceValues(data: FormData, root: HTMLElement, fields: FormField[]): void {
  for (const field of fields) {
    if (field.fieldtype !== 'Checkbox Group') continue;
    const name = API_ALIASES[field.fieldname] || field.fieldname;
    const values = Array.from(root.querySelectorAll<HTMLInputElement>(`input[name="${cssEscape(name)}"]:checked`)).map((input) => input.value);
    data.delete(name);
    data.set(name, values.join(', '));
  }
}

function collectWork(root: HTMLElement, fields: FormField[]): Record<string, string>[] {
  return getWorkGroups(fields).map(({ fields: groupFields }) => {
    const item: Record<string, string> = {};
    for (const [suffix] of groupFields) {
      const target = WORK_FIELD_MAP[suffix] || suffix;
      item[target] = valueOf(root, groupFields.get(suffix)?.fieldname || '');
    }
    return item;
  });
}

function collectEducation(root: HTMLElement): Record<string, string>[] {
  return EDUCATION_PREFIXES.map(([prefix, type]) => ({
    type,
    nameLocation: valueOf(root, `${prefix}_name`),
    lastYear: valueOf(root, `${prefix}_last_year`),
    degree: valueOf(root, `${prefix}_degree`),
    major: valueOf(root, `${prefix}_major`),
  }));
}

function collectAnswers(root: HTMLElement, fields: FormField[]): Record<string, unknown> {
  const answers: Record<string, unknown> = {};
  for (const field of fields) {
    if (field.fieldtype === 'Section Break' || field.fieldtype === 'Signature' || field.fieldtype === 'Attach') continue;
    const name = API_ALIASES[field.fieldname] || field.fieldname;
    if (field.fieldtype === 'Checkbox Group') {
      answers[field.fieldname] = Array.from(root.querySelectorAll<HTMLInputElement>(`input[name="${cssEscape(name)}"]:checked`)).map((input) => input.value);
    } else if (field.fieldtype === 'Check') {
      answers[field.fieldname] = Boolean(root.querySelector<HTMLInputElement>(`input[name="${cssEscape(name)}"]:checked`));
    } else {
      answers[field.fieldname] = valueOf(root, name);
    }
  }
  return answers;
}

function valueOf(root: HTMLElement, name: string): string {
  if (!name) return '';
  return root.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`[name="${cssEscape(name)}"]`)?.value.trim() || '';
}

function showResult(root: HTMLElement, type: 'success' | 'error', message: string): void {
  const result = root.querySelector<HTMLElement>('#result')!;
  result.className = `result ${type}`;
  result.innerHTML = message;
  result.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

function clearResult(root: HTMLElement): void {
  const result = root.querySelector<HTMLElement>('#result')!;
  result.className = 'result';
  result.textContent = '';
}

function today(): string {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function cssEscape(value: string): string {
  return typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(value) : value.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}

function safe(value: unknown): string {
  return String(value || '').replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[character]!));
}

function safeAttr(value: unknown): string {
  return safe(value);
}
